import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Minus } from "lucide-react";
import Seo from "../components/Seo";
import { openSubscribeGate } from "../lib/subscription";

const COMPARISONS = {
  teslita: {
    name: "Teslita",
    title: "EVIQ vs Teslita — snapshot checks vs tracked degradation",
    description:
      "Honest comparison: Teslita gives Tesla owners a free battery snapshot. EVIQ tracks your degradation slope over months, does the warranty math, and signs a passport a buyer can verify.",
    h1: "EVIQ vs Teslita.",
    definition:
      "Teslita is a free tool that reads your Tesla's Fleet telemetry and produces a signed one-time battery health snapshot. EVIQ reads the same telemetry — then keeps reading it, turning months of data into a degradation slope, a warranty forecast, and a renewable Battery Passport.",
    verdict:
      "Honest take: if you want a free, one-time snapshot of a Tesla's battery today, Teslita is genuinely good at that. EVIQ is for owners who want the slope, not the snapshot — where the pack is heading, how far the warranty floor is, and proof that renews every 90 days.",
    rows: [
      ["Price", "Free", "From $9.99/mo · 7-day free preview"],
      ["One-time battery snapshot", true, true],
      ["Tracked degradation slope over months", false, true],
      ["Warranty floor countdown & claim-ready PDF", false, true],
      ["Trim-exact EPA baseline for your config", false, true],
      ["Ford & Volvo/Polestar support", false, true],
      ["Signed, QR-verifiable Battery Passport", "One-time PDF", "Renews every 90 days"],
      ["AI battery coach & charging analysis", false, true],
    ],
    faq: [
      [
        "Is Teslita really free?",
        "Yes. Teslita offers free signed battery snapshots for Tesla vehicles, and that is a fair deal for a point-in-time check. EVIQ charges because continuous tracking, forecasting, and renewable signed passports cost more to run than a one-time scan.",
      ],
      [
        "Why pay for EVIQ when Teslita is free?",
        "A snapshot tells you today's capacity. It cannot tell you your degradation slope, when you will cross your warranty floor, or prove to a buyer that the pack has been healthy for months. If you only need the number once, use the free tool; if you own the car, the slope is what protects your money.",
      ],
      [
        "Does EVIQ support cars Teslita doesn't?",
        "Yes. EVIQ supports Tesla, Ford, and Volvo/Polestar through each manufacturer's official owner-authorized API. Teslita is Tesla-only.",
      ],
    ],
    related: [
      ["/why-eviq", "Why EVIQ, when the car already shows range?"],
      ["/vs/recurrent", "EVIQ vs Recurrent"],
      ["/pricing", "EVIQ plans and pricing"],
    ],
  },
  recurrent: {
    name: "Recurrent",
    title: "EVIQ vs Recurrent — owner telemetry vs shopper-side data",
    description:
      "Honest comparison: Recurrent builds battery reports for EV shoppers and dealers from fleet models. EVIQ reads your own car's telemetry, owner-authorized, and signs a passport buyers can verify.",
    h1: "EVIQ vs Recurrent.",
    definition:
      "Recurrent is a battery-report service used by EV shoppers and dealerships — it estimates a used EV's range and battery condition from fleet models and observed range data. EVIQ works from the owner's side: direct, owner-authorized telemetry from the car itself, turned into exact health figures and a signed Battery Passport.",
    verdict:
      "Honest take: shopping at a dealership? You may well see a Recurrent report, and it is the biggest name in shopper-side battery data. Selling or keeping your own EV, with exact figures from your own pack that a buyer can verify by QR? That is what EVIQ was built for.",
    rows: [
      ["Built for", "Shoppers & dealers", "Owners (and their buyers)"],
      ["Data source", "Fleet models + observed range", "Your car's own telemetry, owner-authorized"],
      ["Exact pack state of health", "Modeled estimate", "Measured, trim-exact EPA baseline"],
      ["Warranty floor countdown & claim PDF", false, true],
      ["Signed, QR-verifiable Battery Passport", false, true],
      ["Dealer network reports", true, false],
      ["Tesla, Ford, Volvo/Polestar via official APIs", "Varies by model", true],
      ["Price", "Free for shoppers (dealer-funded)", "From $9.99/mo · $19.99 Single Report"],
    ],
    faq: [
      [
        "Is EVIQ a replacement for a Recurrent report?",
        "Different sides of the same sale. A Recurrent report estimates a car's battery from the outside; an EVIQ Battery Passport is generated from the car's own telemetry with the owner's consent and carries a verifiable signature. The strongest used-EV listing shows both.",
      ],
      [
        "Which is more accurate?",
        "A model estimating from fleet data cannot beat the car's own telemetry for that specific pack. EVIQ's figures come from your VIN's actual readings against its exact EPA trim baseline; Recurrent's strength is breadth across listings it has never connected to.",
      ],
      [
        "Can a buyer verify an EVIQ passport?",
        "Yes — every passport carries a QR code and an HMAC signature a buyer can check in seconds at eviq.pro, free. No account needed.",
      ],
    ],
    related: [
      ["/vs/teslita", "EVIQ vs Teslita"],
      ["/vin", "Check a used EV's battery by VIN"],
      ["/passport/demo", "See a sample Battery Passport"],
    ],
  },
};

const Cell = ({ v }) => {
  if (v === true) return <Check className="w-4 h-4 mx-auto" style={{ color: "#39FF14" }} strokeWidth={3} />;
  if (v === false) return <Minus className="w-4 h-4 mx-auto text-zinc-600" strokeWidth={2.5} />;
  return <span className="text-[12px] text-zinc-300">{v}</span>;
};

export default function ComparisonPage() {
  const { competitor } = useParams();
  const data = COMPARISONS[competitor];
  if (!data) return <Navigate to="/" replace />;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: data.faq.map(([q, a]) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="comparison-page">
      <Seo
        title={data.title}
        description={data.description}
        canonical={`https://eviq.pro/vs/${competitor}`}
        jsonLd={jsonLd}
      />
      <div className="max-w-3xl mx-auto px-5 py-10">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors" data-testid="comparison-back">
          <ArrowLeft className="w-4 h-4" /> Back to eviq.pro
        </Link>

        <div className="mt-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-zinc-500">Honest comparison</div>
          <h1 className="font-display mt-2 text-3xl sm:text-4xl font-semibold leading-tight">
            EVIQ vs <span className="brand-text-gradient">{data.name}.</span>
          </h1>
          <p className="mt-4 text-sm text-zinc-400 leading-relaxed">{data.definition}</p>
          <p className="mt-3 text-sm text-zinc-300 leading-relaxed rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3">
            {data.verdict}
          </p>
        </div>

        <div className="mt-8 overflow-x-auto border border-white/10 rounded-xl bg-white/[0.02]">
          <table className="w-full text-sm min-w-[480px]" data-testid="comparison-table">
            <thead>
              <tr className="border-b border-white/10 font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                <th className="px-4 py-3 text-left font-medium">Capability</th>
                <th className="px-4 py-3 text-center font-medium">{data.name}</th>
                <th className="px-4 py-3 text-center font-medium text-white">EVIQ</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map(([label, them, us]) => (
                <tr key={label} className="border-b border-white/5 last:border-0">
                  <td className="px-4 py-3 text-zinc-300 text-[13px]">{label}</td>
                  <td className="px-4 py-3 text-center"><Cell v={them} /></td>
                  <td className="px-4 py-3 text-center"><Cell v={us} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-10">
          <h2 className="font-display text-xl font-semibold text-white">Questions owners ask</h2>
          <div className="mt-4 space-y-3">
            {data.faq.map(([q, a]) => (
              <div key={q} className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="comparison-faq-item">
                <div className="text-sm text-white font-medium">{q}</div>
                <p className="mt-1.5 text-[13px] text-zinc-400 leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 rounded-2xl border border-white/10 bg-white/[0.02] p-6 text-center">
          <div className="text-sm text-white font-medium">See your own pack's numbers.</div>
          <p className="mt-1 text-xs text-zinc-500">7-day free preview · Card required · Cancel by email in one line</p>
          <button
            type="button"
            onClick={() => openSubscribeGate(`vs-${competitor}`)}
            data-testid="comparison-cta"
            className="mt-4 brand-gradient text-black font-semibold rounded-md px-6 py-2.5 text-sm inline-flex items-center gap-2 hover:opacity-90 transition-opacity"
          >
            Start 7-day free preview <ArrowRight className="w-4 h-4" strokeWidth={2.4} />
          </button>
        </div>

        <div className="mt-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">More from EVIQ</div>
          <ul className="space-y-1.5 text-sm">
            {data.related.map(([href, text]) => (
              <li key={href}>
                <Link to={href} className="text-[#39FF14] hover:underline">{text}</Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
